import { Divider, DropDownButton, DropdownItem, IconName, Stack } from '@optimus-web/core';
import { lt, showSuccessAlert } from '@mews/framework';
import { EnterpriseLinkIconButton, TooltippedIconButton } from '@mews-commander/ui';
import { copyToClipboard } from '@mews-commander/utils';
import { DataTestId } from '@mews-commander/enums';
import { useEditRateDialogForm } from '../forms/hooks/useEditRateDialogForm';
import { useUpdateDefault, useUpdateEnabled } from '../../mutations';
import { useDeleteRateDialog } from '../forms/hooks/useDeleteRateDialog';

interface Props {
    rateId: string;
    rateName: string;
    isEnabled: boolean;
    closeSidePanel: () => void;
}

export const RateSidePanelActions = ({ rateId, rateName, isEnabled, closeSidePanel }: Props) => {
    const [renderDialog, toggleDialog] = useEditRateDialogForm(rateId);

    const { mutate: updateDefault } = useUpdateDefault(rateName);

    const { mutate: updateEnabled } = useUpdateEnabled(!!isEnabled);

    const handleUpdateDefault = () => updateDefault({ rateId });

    const handleUpdateEnabled = () => {
        updateEnabled({ rateId, isEnabled: !isEnabled });
    };

    const { confirmationDialog, openConfirmationDialog } = useDeleteRateDialog({
        rateName,
        rateId,
        onSuccess: closeSidePanel,
    });

    return (
        <>
            <Stack spacing="100">
                <TooltippedIconButton
                    data-test-id={DataTestId.ServiceRatesEditRateDetailsButton}
                    icon={IconName.Edit}
                    size="small"
                    onClick={toggleDialog}
                    tooltip={lt('Edit')}
                    accessibleText={lt('EditRate')}
                    type="button"
                />
                <EnterpriseLinkIconButton
                    icon={IconName.Pricing}
                    tooltip={lt('Pricing')}
                    accessibleText={lt('Pricing')}
                    size="small"
                    type="button"
                    to="Rate/Pricing/$rateId"
                    params={{ rateId }}
                />
                <DropDownButton icon={IconName.More} size="small" variant="tertiary">
                    <DropdownItem
                        label={lt('CopyRateId')}
                        onClick={() => {
                            if (copyToClipboard(rateId)) {
                                showSuccessAlert({ description: lt('CopiedSuccessMessage') });
                            }
                        }}
                    />
                    {isEnabled ? (
                        <DropdownItem label={lt('Deactivate')} onClick={handleUpdateEnabled} />
                    ) : (
                        <DropdownItem label={lt('Activate')} onClick={handleUpdateEnabled} />
                    )}
                    <DropdownItem label={lt('SetAsDefault')} onClick={handleUpdateDefault} />
                    <Divider />
                    <DropdownItem label={lt('DeleteRate')} variant="danger" onClick={openConfirmationDialog} />
                </DropDownButton>
            </Stack>
            {renderDialog()}
            {confirmationDialog}
        </>
    );
};
